/** * Componente de notificações toast * Exibe mensagens temporárias para feedback ao usuário * *
Features: * - Múltiplos tipos de mensagem (sucesso, erro, aviso, info) * - Animações de
entrada/saída * - Responsividade * - Auto-remoção * - Clique para fechar */

<template>
    <div class="toast-container">
        <TransitionGroup name="toast">
            <div
                v-for="mensagem in mensagens"
                :key="mensagem.id"
                :class="['toast', `toast--${mensagem.tipo}`]"
            >
                <!-- Exibe o ícone dinamicamente com base no tipo da mensagem -->
                <div class="toast_conteudo">
                    <span
                        class="material-symbols-outlined icone"
                        :class="{ 'icone--info': mensagem.tipo === 'info' }"
                    >
                        {{ icones[mensagem.tipo] }}
                    </span>
                    <div class="toast_mensagens">
                        <span class="toast__titulo">
                            {{ traduzirTexto(titulos[mensagem.tipo] || titulos.info) }}
                        </span>

                        <span class="toast__texto">
                            {{ traduzirTexto(mensagem.texto) }}
                        </span>
                    </div>
                </div>
                <!-- Mova o evento de clique para o ícone de fechar -->
                <span
                    id="close_toast"
                    class="material-symbols-outlined close"
                    role="button"
                    tabindex="0"
                    :title="$t('toast.close')"
                    :aria-label="$t('toast.close')"
                    @click="removerMensagem(mensagem.id)"
                    @keydown.enter.prevent="removerMensagem(mensagem.id)"
                    @keydown.space.prevent="removerMensagem(mensagem.id)"
                >
                    close
                </span>
                <!-- Barra de progresso para auto-remoção -->
                <div
                    class="toast__progresso"
                    :style="{ backgroundColor: coresBorda[mensagem.tipo] }"
                    @animationend="removerMensagem(mensagem.id)"
                ></div>
            </div>
        </TransitionGroup>
    </div>
</template>

<script>
    import { defineComponent, computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { TransitionGroup } from 'vue'
    import { useToastStore } from '@/stores'
    import '@/assets/scss/icons.scss'

    export default defineComponent({
        name: 'Toast',
        components: {
            TransitionGroup,
        },
        setup() {
            const { t, te } = useI18n()
            const toastStore = useToastStore()

            /**
             * Lista de mensagens ativas
             * @type {ComputedRef<Array>}
             */
            const mensagens = computed(() => toastStore.mensagens)
            /**
             * Remove uma mensagem específica
             * @param {number} id - ID da mensagem a ser removida
             */
            const removerMensagem = (id) => {
                toastStore.removerMensagem(id)
            }

            const traduzirTexto = (valor) => {
                if (!valor) return ''

                if (typeof valor !== 'string') {
                    return valor
                }

                return te(valor) ? t(valor) : valor
            }

            /**
             * Mapeamento de ícones para cada tipo de toast
             * @type {Object}
             */
            const icones = {
                sucesso: 'check_circle', // Ícone para sucesso
                erro: 'gpp_maybe', // Ícone para erro
                aviso: 'warning', // Ícone para aviso
                info: 'feedback', // Ícone para informação
            }

            // Títulos para cada tipo de toast
            const titulos = {
                sucesso: 'toast.success',
                erro: 'toast.error',
                aviso: 'toast.warning',
                info: 'toast.info',
            }

            // Cores da borda para cada tipo de toast
            const coresBorda = {
                sucesso: '#d1df99', // Cor da borda para sucesso
                erro: '#ff7a7c', // Cor da borda para erro
                aviso: '#f8ed8d', // Cor da borda para aviso
                info: '#7ebced', // Cor da borda para informação
            }

            return {
                mensagens,
                removerMensagem,
                icones,
                titulos,
                coresBorda,
                traduzirTexto,
            }
        },
    })
</script>

<style lang="scss" scoped>
    .toast-container {
        position: fixed;
        top: 43px;
        right: 0;
        z-index: 99999;
        display: flex;
        width: 450px;
        height: 83px;
        flex-direction: column;
        gap: 10px;
        pointer-events: none;

        // Media query para dispositivos móveis (até 768px)
        @media (max-width: 799px) {
            top: 20px;
            left: 50%;
            right: auto;
            transform: translateX(-50%);
            width: 90vw; // 90% da largura da viewport
            max-width: 90%; // Garante que não ultrapasse 90%
        }
    }

    .toast {
        position: relative; // Para posicionar a barrinha de progresso
        display: flex;
        justify-content: space-between;
        pointer-events: auto;
        margin-right: -5px;
        min-width: 250px;
        padding: 15px 20px;
        border-radius: 4px;
        cursor: pointer;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
        opacity: 1 !important;

        // Ajuste para mobile
        @media (max-width: 799px) {
            min-width: unset; // Remove a largura mínima
            width: 100%;
            box-sizing: border-box;
            text-align: center; // Centraliza o texto
        }

        &--sucesso {
            background-color: #a2ca01;
            color: white;
            border: 3px solid #d1df99;
        }

        &--erro {
            background-color: #a80808;
            color: white;
            border: 3px solid #ff7a7c;
        }

        &--aviso {
            background-color: #e6c407;
            color: white;
            border: 3px solid #f8ed8d;
        }

        &--info {
            background-color: #3687c7;
            color: white;
            border: 3px solid #7ebced;
        }

        &--sucesso,
        &--erro,
        &--aviso,
        &--info {
            border-right: none;
        }
    }

    .toast_conteudo {
        display: flex;
        align-items: center;
        justify-content: left;
        gap: 10px;
    }

    .material-symbols-outlined.icone {
        color: white;
        font-size: 32px;
        font-variation-settings:
            'FILL' 1,
            'wght' 700,
            'GRAD' 0,
            'opsz' 24;

        &.icone--info {
            transform: rotateY(180deg); // Gira o ícone 180 graus
        }
    }

    .material-symbols-outlined.close {
        font-size: 25px;
        height: 25px;
    }

    .toast_mensagens {
        display: flex;
        flex-direction: column;
    }

    .toast__titulo {
        font-weight: bold;
        font-size: 1rem;
    }

    .toast__texto {
        font-size: 0.8125rem; // 13px
        font-weight: normal;
    }

    .toast__progresso {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 3px;
        // background-color: transparent;
        animation: progresso 5s linear forwards;
    }

    @keyframes progresso {
        from {
            width: 0%;
        }
        to {
            width: 100%;
        }
    }

    .toast-enter-active,
    .toast-leave-active {
        transition: all 0.3s ease;
    }

    .toast-enter-from,
    .toast-leave-to {
        opacity: 0;

        @media (min-width: 769px) {
            // Desktop: animação da direita para esquerda
            transform: translateX(100%);
        }

        @media (max-width: 768px) {
            // Mobile: animação de cima para baixo
            transform: translateY(-20px);
        }
    }

    .toast-enter-to,
    .toast-leave-from {
        opacity: 1;
        transform: translateX(0);
    }
</style>
